import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { meQueryKey } from '@/auth/me'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/cn'
import { supabase } from '@/lib/supabase'

type FlagRow = {
  key: string
  enabled: boolean
  roles: string[] | null
  label: string
  description: string | null
  updated_at: string
}

const flagsQuery = queryOptions({
  queryKey: ['admin', 'flags'],
  queryFn: async () => {
    const { data, error } = await supabase.from('feature_flags').select('*').order('label')
    if (error) throw error
    return data as FlagRow[]
  },
})

const rolesQuery = queryOptions({
  queryKey: ['roles'],
  queryFn: async () => {
    const { data, error } = await supabase.from('roles').select('key, label').order('sort_order')
    if (error) throw error
    return data as { key: string; label: string }[]
  },
  staleTime: 60 * 60_000,
})

export const Route = createFileRoute('/_app/admin/flags')({ component: Flags })

/**
 * Feature switches. Super admin can turn any feature or field tool on or off, for everyone or for
 * chosen roles only (e.g. test the strip calculator with just the admin role first).
 */
function Flags() {
  const flags = useQuery(flagsQuery)
  const roles = useQuery(rolesQuery)
  const queryClient = useQueryClient()

  const save = useMutation({
    mutationFn: async (f: Pick<FlagRow, 'key' | 'enabled' | 'roles'>) => {
      const { error } = await supabase.rpc('admin_set_flag', {
        flag_key: f.key,
        flag_enabled: f.enabled,
        flag_roles: f.roles,
      })
      if (error) throw error
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: flagsQuery.queryKey })
      void queryClient.invalidateQueries({ queryKey: meQueryKey })
    },
  })

  const toggleRole = (f: FlagRow, role: string) => {
    const all = roles.data?.map((r) => r.key) ?? []
    const current = f.roles ?? all
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role]
    save.mutate({ key: f.key, enabled: f.enabled, roles: next.length === all.length ? null : next })
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl uppercase">Features</h1>
        <p className="text-fg-muted">
          Switch features and field tools on or off. Pick roles to limit who sees a feature. Every
          change is recorded in the audit log.
        </p>
      </div>
      {(flags.error || save.error) && (
        <p role="alert" className="font-semibold text-danger">
          {(flags.error ?? save.error)?.message}
        </p>
      )}
      <ul className="grid gap-4 xl:grid-cols-2">
        {flags.data?.map((f) => (
          <li key={f.key}>
            <Card className="flex h-full flex-col gap-3">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold">{f.label}</h2>
                  {f.description && <p className="text-sm text-fg-muted">{f.description}</p>}
                </div>
                <button
                  role="switch"
                  aria-checked={f.enabled}
                  aria-label={`${f.label} ${f.enabled ? 'on' : 'off'}`}
                  onClick={() => save.mutate({ key: f.key, enabled: !f.enabled, roles: f.roles })}
                  className={cn(
                    'relative h-8 w-14 shrink-0 rounded-full transition-colors',
                    f.enabled ? 'bg-brand' : 'bg-border-strong',
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-1 left-0 size-6 rounded-full bg-white shadow transition-transform',
                      f.enabled ? 'translate-x-7' : 'translate-x-1',
                    )}
                  />
                </button>
              </div>
              <fieldset className="flex flex-wrap gap-2" disabled={!f.enabled}>
                <legend className="sr-only">Roles for {f.label}</legend>
                {roles.data?.map((r) => {
                  const on = f.roles === null || f.roles.includes(r.key)
                  return (
                    <button
                      key={r.key}
                      aria-pressed={on}
                      onClick={() => toggleRole(f, r.key)}
                      className={cn(
                        'min-h-10 rounded-full border-2 px-3 text-sm font-semibold disabled:opacity-40',
                        on ? 'border-fg bg-fg text-bg' : 'border-border text-fg-muted',
                      )}
                    >
                      {r.label}
                    </button>
                  )
                })}
              </fieldset>
            </Card>
          </li>
        ))}
      </ul>
    </div>
  )
}
